import { useState, useEffect, useContext } from "react";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ModuleDto, SessionDto, SessionEventDto, UserDto, UserModuleDto } from "../../general/dtos";
import { UserManagementDataContext, UserManagementDataContextValue } from "../../providers/user-management-provider";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism"
import { bookmarkEventKey, useScopedBookmarkSelection } from '../../general/bookmark-selection';
import { Button } from 'primereact/button';
import { useCompactViewport } from '../../general/use-compact-viewport';
import '../../assets/user-details.css';

interface Props {
  currentUser: UserDto;
  loadSessions: (moduleKey: string) => Promise<SessionDto[]>;
  loadEvents: (sessionId: string) => Promise<SessionEventDto[]>;
}

export function UserSessionExplorer({
  currentUser,
  loadSessions,
  loadEvents
}: Props) {
  const ctx: UserManagementDataContextValue | null = useContext<UserManagementDataContextValue | null>(UserManagementDataContext);
  const { allModules, userModules } = ctx!;

  const [moduleKey, setModuleKey] = useScopedBookmarkSelection('activity_lesson', currentUser.sub);
  const selectedModule = allModules.find(module => module.module_key === moduleKey) ?? null;
  const setSelectedModule = (module: ModuleDto | null) => setModuleKey(module?.module_key ?? null);
  const [filteredUserModules, setFilteredUserModules] = useState<ModuleDto[] | null>(null);
  const [sessions, setSessions] = useState<SessionDto[]>([]);
  const [sessionKey, setSessionKey] = useScopedBookmarkSelection('activity_session', `${currentUser.sub}:${moduleKey ?? ''}`);
  const selectedSession = sessions.find(session => session.session_id === sessionKey) ?? null;
  const setSelectedSession = (session: SessionDto | null) => setSessionKey(session?.session_id ?? null);

  const [events, setEvents] = useState<SessionEventDto[]>([]);
  const [eventTime, setEventTime] = useScopedBookmarkSelection('activity_event', `${currentUser.sub}:${sessionKey ?? ''}`);
  const selectedEvent = events.find(event => bookmarkEventKey(event) === eventTime) ?? null;
  const setSelectedEvent = (event: SessionEventDto | null) => setEventTime(event ? bookmarkEventKey(event) : null);
  const [selectedPayload, setSelectedPayload] = useState<string>('');

  const [loadingSessions, setLoadingSessions] = useState<boolean>(false);
  const [loadingEvents, setLoadingEvents] = useState<boolean>(false);
  const compact = useCompactViewport();
  const [mobilePanel, setMobilePanel] = useState<'sessions' | 'events' | 'details'>(eventTime ? 'details' : sessionKey ? 'events' : 'sessions');
  useEffect(() => {
    setMobilePanel(eventTime ? 'details' : sessionKey ? 'events' : 'sessions');
  }, [currentUser.sub, moduleKey, sessionKey, eventTime]);

  useEffect(() => {
    setSessions([]);
    setEvents([]);
    if (!currentUser) {
        return;
    }

    if (userModules) {
        const userMods: UserModuleDto[] = userModules[currentUser.sub];
        if (userMods) {
            const filtered: ModuleDto[] = allModules.filter((m) => userMods.find((a) => a.module_key === m.module_key));
            setFilteredUserModules(filtered);
        }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, allModules, userModules]);

  // Load sessions when module changes
  useEffect(() => {
    setSessions([]);
    setEvents([]);
    if (!selectedModule) {
        return;
    }

    setLoadingSessions(true);
    let active = true;
    loadSessions(selectedModule.module_key).then((data) => {
      if (!active) return;
      setSessions(data);
      setLoadingSessions(false);
    });
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModule]);

  // Load events when session changes
  useEffect(() => {
    setEvents([]);
    if (!selectedSession) {
        return;
    }

    setLoadingEvents(true);
    let active = true;
    loadEvents(selectedSession.session_id).then((data) => {
      if (!active) return;
      setEvents(data);
      setLoadingEvents(false);
    });
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession]);

  const formatPayload = (payload: string | null): string => {
    const result: string = payload ?? '';
    if (result.length > 2 && result[0] === '{' && result[result.length - 1] === '}') {
        try {
            const o: any = JSON.parse(result);
            return JSON.stringify(o, null, 2);
        } catch (error) {
            return result;
        }
    }
    return result;
  }

  useEffect(() => {
    setSelectedPayload(formatPayload(selectedEvent?.payload!));
  }, [selectedEvent]);

  const isJson: boolean = (selectedPayload.length > 2 && selectedPayload[0] === '{' && selectedPayload[selectedPayload.length - 1] === '}');

  return (
    <div className="user-session-explorer">
      <div className="user-session-module-picker">
        <Dropdown
          aria-label="Activity module"
          value={selectedModule}
          options={filteredUserModules ?? []}
          optionLabel="name"
          placeholder="Select module"
          onChange={(e) => { setSelectedModule(e.value); setMobilePanel('sessions'); }}
          scrollHeight={'500px'}
        />
      </div>

      {compact && <div className="user-session-mobile-tabs" role="group" aria-label="Session explorer view">
        <button type="button" aria-pressed={mobilePanel === 'sessions'} onClick={() => setMobilePanel('sessions')}>Sessions</button>
        <button type="button" aria-pressed={mobilePanel === 'events'} disabled={!selectedSession}
          onClick={() => setMobilePanel('events')}>Events</button>
        <button type="button" aria-pressed={mobilePanel === 'details'} disabled={!selectedEvent}
          onClick={() => setMobilePanel('details')}>Event details</button>
      </div>}
      <div className="user-session-panes">
        
        <section className="user-sessions-pane" aria-label="Session list" hidden={compact && mobilePanel !== 'sessions'}>
          <DataTable
            value={sessions}
            key={'started_at'}
            loading={loadingSessions}
            selectionMode="single"
            selection={selectedSession}
            dataKey="session_id"
            onSelectionChange={(e) => {
              const session = (e.value ?? selectedSession) as SessionDto | null;
              if (session) { setSelectedSession(session); setMobilePanel('events'); }
            }}
            scrollable
            scrollHeight="flex"
            size="small"
            emptyMessage={selectedModule ? 'No sessions recorded for this module.' : 'Select a module to view its sessions.'}
          >
            <Column field="started_at" header="Started" bodyClassName="activity-timestamp" />
            <Column field="finished_at" header="Finished" bodyClassName="activity-timestamp" />
          </DataTable>
        </section>

        <div className="user-session-event-panes" hidden={compact && mobilePanel === 'sessions'}>
          <section className="user-session-events" aria-label="Session events" hidden={compact && mobilePanel !== 'events'}>
            {compact && <Button type="button" label="Back to sessions" icon="pi pi-arrow-left" text size="small"
              className="session-explorer-back" onClick={() => setMobilePanel('sessions')} />}
            <DataTable
              value={events}
              key={'occured_at'}
              loading={loadingEvents}
              selectionMode="single"
              selection={selectedEvent}
              onSelectionChange={(e) => {
                const event = (e.value ?? selectedEvent) as SessionEventDto | null;
                if (event) { setSelectedEvent(event); setMobilePanel('details'); }
              }}
              scrollable
              scrollHeight="flex"
              size="small"
              emptyMessage={selectedSession ? 'No events recorded for this session.' : 'Select a session to view its events.'}
            >
              <Column field="occured_at" header="Timestamp" bodyClassName="activity-timestamp" />
              <Column header="Event" body={(row) => row.payload.slice(0, 40) + "…"} />
            </DataTable>
          </section>

          <section className="user-session-event-details" aria-label="Event details" hidden={compact && mobilePanel !== 'details'}>
            {compact && <Button type="button" label="Back to events" icon="pi pi-arrow-left" text size="small"
              className="session-explorer-back" onClick={() => setMobilePanel('events')} />}
            { !selectedEvent ? <p className="session-explorer-empty">Select an event to inspect its details.</p> : isJson ? (<>
                <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <SyntaxHighlighter language="json" style={oneDark} customStyle={{ margin: 0, height: "100%", overflow: 'auto' }}>
                        {selectedPayload}
                    </SyntaxHighlighter>
                </div></>) : (<>
                    <textarea
                    aria-label="Event payload"
                    readOnly
                    value={selectedPayload}
                    style={{
                        width: "100%",
                        height: "100%",
                        resize: "none",
                        padding: "0.75rem",
                        fontFamily: "monospace",
                        fontSize: "0.85rem",
                        whiteSpace: "pre-wrap",
                        background: "var(--surface-ground)",
                        border: "1px solid var(--surface-border)",
                        borderRadius: "4px"
                    }}
                    />
                </>)
            }
          </section>
        </div>
      </div>
    </div>
  );
}
